import { createRoute, useNavigate } from "@tanstack/solid-router";
import { appRoute } from "./route";
import {
  createEffect,
  createResource,
  createSignal,
  For,
  on,
  onCleanup,
  Show,
  Suspense,
  untrack,
} from "solid-js";
import {
  Book,
  ChevronRight,
  Compass,
  Info,
  RefreshCw,
  Search as SearchIcon,
  X,
} from "@fleet/icons";
import { api } from "@/lib/api";
import { handleDefinitionLinkClick } from "@/utils/dictionary-links";
import { NativeAdCard } from "@/features/growth/components/native-ad-card";
import type { components } from "@bso/client";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";

type DictionaryEntryResponse = components["schemas"]["DictionaryEntryResponse"];
type DictionaryPage = { items: DictionaryEntryResponse[]; nextCursor: string | null };
type ExtraPage = DictionaryPage & { query: string };
const EMPTY_PAGE: DictionaryPage = { items: [], nextCursor: null };

export function DictionaryPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = dictionaryRoute.useSearch();

  // Selected Strong word (`?word=`) and search query (`?q=`) live
  // in URL so dictionary term is **shareable/reloadable**
  // (e.g. `/dictionary?word=H1254`). Local signals are reactive source
  // render state; initialized from URL and `createEffect` reflects external
  // URL->signal changes (back/forward + pasted link). Idempotent on reload.
  const [query, setQuery] = createSignal(search().q ?? "");
  const [debouncedQuery, setDebouncedQuery] = createSignal(search().q ?? "");
  const [selectedWord, setSelectedWordSignal] = createSignal<string | null>(search().word ?? null);

  function setSelectedWord(word: string | null) {
    setSelectedWordSignal(word);
    void navigate({
      to: "/dictionary",
      search: (prev) => ({ ...prev, word: word ?? undefined }),
      replace: true,
    });
  }

  function setQueryAndUrl(value: string) {
    setQuery(value);
    void navigate({
      to: "/dictionary",
      search: (prev) => ({ ...prev, q: value.trim() === "" ? undefined : value }),
      replace: true,
    });
  }

  // URL → signals: load via URL, back/forward navigation or pasted link.
  // NOTE: comparison wrapped in `untrack` so effect ONLY reacts to
  // EXTERNAL URL changes. If tracking signal it writes itself, a
  // state-first change (typing + async navigate) re-triggers with URL STILL
  // old and REVERTS freshly written signal (bug #15b / CC-16). Same pattern
  // already applied in font-manager.tsx.
  createEffect(() => {
    const urlWord = search().word ?? null;
    if (untrack(() => urlWord !== selectedWord())) {
      setSelectedWordSignal(urlWord);
    }
  });
  createEffect(() => {
    const urlQuery = search().q ?? "";
    if (untrack(() => urlQuery !== query())) {
      setQuery(urlQuery);
    }
  });

  const DEBOUNCE_DELAY_MS = 250;
  // Debounce query inputs
  createEffect(() => {
    const q = query();
    const timer = setTimeout(() => {
      setDebouncedQuery(q);
    }, DEBOUNCE_DELAY_MS);
    onCleanup(() => {
      clearTimeout(timer);
    });
  });

  // Fetch the first page of matches. "Load more" below appends the rest.
  const [firstPage] = createResource(debouncedQuery, async (q): Promise<DictionaryPage> => {
    if (!q.trim()) {
      return EMPTY_PAGE;
    }
    try {
      const { data, response } = await api.GET("/api/v1/bible/dictionary/search", {
        params: {
          query: {
            query: q,
            limit: 30,
          },
        },
      });
      if (response.ok && data) {
        return { items: data.items, nextCursor: data.nextCursor ?? null };
      }
    } catch (e) {
      console.warn("Failed to search dictionary via API, trying local offline fallback", e);
    }

    // Offline fallback: if the query itself is a valid Strong code (e.g. H1234 or G5678)
    if (typeof globalThis !== "undefined" && "__TAURI_INTERNALS__" in globalThis) {
      const cleaned = q.trim().toUpperCase();
      if (/^[HG]\d+$/.test(cleaned)) {
        try {
          const { invoke } = await import("@tauri-apps/api/core");
          const nativeDef = await invoke<DictionaryEntryResponse | null>("get_definition_native", {
            word: cleaned,
          });
          if (nativeDef) {
            return { items: [nativeDef], nextCursor: null };
          }
        } catch (err) {
          console.error("Local offline fallback search failed", err);
        }
      }
    }
    return EMPTY_PAGE;
  });

  // Pages after the first. Each one is tagged with the query it was fetched for, so a
  // page that lands after the query changed is dropped instead of joining the new results.
  const [extraPages, setExtraPages] = createSignal<ExtraPage | null>(null);
  const [loadingMore, setLoadingMore] = createSignal(false);
  createEffect(on(debouncedQuery, () => setExtraPages(null), { defer: true }));

  const currentExtra = (): ExtraPage | null => {
    const extra = extraPages();
    return extra && extra.query === debouncedQuery() ? extra : null;
  };
  const matches = () => [...(firstPage()?.items ?? []), ...(currentExtra()?.items ?? [])];
  const nextCursor = (): string | null => {
    const extra = currentExtra();
    return extra ? extra.nextCursor : (firstPage()?.nextCursor ?? null);
  };

  async function loadMore() {
    const q = debouncedQuery();
    const cursor = nextCursor();
    if (!q.trim() || !cursor || loadingMore()) {
      return;
    }
    setLoadingMore(true);
    try {
      const { data, response } = await api.GET("/api/v1/bible/dictionary/search", {
        params: { query: { query: q, limit: 30, cursor } },
      });
      if (response.ok && data && debouncedQuery() === q) {
        const earlier = currentExtra()?.items ?? [];
        setExtraPages({
          query: q,
          items: [...earlier, ...data.items],
          nextCursor: data.nextCursor ?? null,
        });
      }
    } catch (e) {
      console.warn("Failed to load more dictionary results", e);
    } finally {
      setLoadingMore(false);
    }
  }

  // Fetch full definition for selected word
  const [definition] = createResource(
    selectedWord,
    async (word): Promise<DictionaryEntryResponse | null> => {
      if (!word) {
        return null;
      }

      // Try local tauri command if running inside Tauri
      if (typeof globalThis !== "undefined" && "__TAURI_INTERNALS__" in globalThis) {
        try {
          const { invoke } = await import("@tauri-apps/api/core");
          const nativeDef = await invoke<DictionaryEntryResponse | null>("get_definition_native", {
            word,
          });
          if (nativeDef) {
            return nativeDef;
          }
        } catch (e) {
          console.warn("Failed to get definition natively, falling back to HTTP api", e);
        }
      }

      const { data, response } = await api.GET("/api/v1/bible/dictionary/definition/{word}", {
        params: {
          path: {
            word,
          },
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch definition");
      }
      return data ?? null;
    },
  );

  // Listen for Escape key to clear selection
  createEffect(() => {
    if (selectedWord()) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setSelectedWord(null);
        }
      };
      globalThis.addEventListener("keydown", handleKeyDown);
      onCleanup(() => {
        globalThis.removeEventListener("keydown", handleKeyDown);
      });
    }
  });

  return (
    <div class="flex flex-col h-full w-full bg-background text-foreground overflow-hidden">
      {/* Header */}
      <div class="border-b border-border/40 bg-card/30 backdrop-blur-lg px-4 md:px-8 py-4 md:py-5 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
          <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
            <Book class="h-5 w-5" />
          </div>
          <div>
            <h1 class="text-xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-transparent">
              {t(msg`Strong's Dictionary`)}
            </h1>
            <p class="text-xs text-muted-foreground mt-0.5">
              {t(msg`Complete definitions in Hebrew and Greek`)}
            </p>
          </div>
        </div>
      </div>

      {/* Split Responsive Container */}
      <div class="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Left Search Pane - Hidden on mobile if word is selected */}
        <div
          class="w-full md:w-80 border-b md:border-b-0 md:border-r border-border/40 flex flex-col bg-card/10 shrink-0"
          classList={{
            "hidden md:flex": Boolean(selectedWord()),
            flex: !selectedWord(),
          }}
        >
          <div class="p-4 border-b border-border/30">
            <div class="relative">
              <SearchIcon class="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={t(msg`Search code (H1254) or word...`)}
                value={query()}
                onInput={(e) => setQueryAndUrl(e.target.value)}
                class="w-full pl-10 pr-8 py-2 bg-background border border-border/45 rounded-lg text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/25 transition-all"
              />
              <Show when={query().length > 0}>
                <button
                  type="button"
                  onClick={() => {
                    setQueryAndUrl("");
                  }}
                  class="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 hover:bg-muted text-muted-foreground rounded-full"
                >
                  <X class="w-3 h-3" />
                </button>
              </Show>
            </div>
          </div>

          {/* Autocomplete Predictive List */}
          <div class="flex-1 overflow-y-auto p-2">
            <Suspense
              fallback={
                <div class="space-y-2 p-2 animate-pulse">
                  <div class="h-10 bg-muted/65 rounded-lg" />
                  <div class="h-10 bg-muted/65 rounded-lg" />
                  <div class="h-10 bg-muted/65 rounded-lg" />
                </div>
              }
            >
              <Show
                when={matches().length > 0 ? matches() : undefined}
                fallback={
                  <div class="flex flex-col items-center justify-center py-16 text-center p-4">
                    <Compass class="h-8 w-8 text-muted-foreground/45 mb-2" />
                    <p class="text-xs font-semibold text-foreground/70">{t(msg`No results`)}</p>
                    <p class="text-2xs text-muted-foreground mt-0.5">
                      {t(msg`Type to find keywords in the dictionary.`)}
                    </p>
                  </div>
                }
              >
                <div class="space-y-1">
                  {/* The native card lives in the dictionary catalogue: it sits after
                      the last result, reserves its space (CLS=0) and collapses to
                      `null` without a fill. It never goes inside a lexicon entry (that
                      is a STUDY surface) nor between results, which would break the scan. */}
                  <For each={matches()}>
                    {(item) => (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedWord(item.word);
                        }}
                        class="w-full flex items-center justify-between p-3 rounded-lg text-left transition-all border border-transparent select-none active:scale-[0.99]"
                        classList={{
                          "bg-primary/10 border-primary/20 text-primary":
                            selectedWord() === item.word,
                          "hover:bg-muted/40 text-foreground/85": selectedWord() !== item.word,
                        }}
                      >
                        <div class="flex flex-col min-w-0 pr-2">
                          <span class="text-xs font-bold font-mono tracking-wide">{item.word}</span>
                          <span class="text-3xs text-muted-foreground truncate font-sans mt-0.5">
                            {item.definition ? item.definition.replaceAll(/<[^>]*>/g, "") : ""}
                          </span>
                        </div>
                        <ChevronRight class="w-3.5 h-3.5 shrink-0 opacity-70" />
                      </button>
                    )}
                  </For>
                  <Show when={nextCursor()}>
                    <button
                      type="button"
                      disabled={loadingMore()}
                      onClick={() => {
                        void loadMore();
                      }}
                      class="w-full p-3 rounded-lg text-xs font-semibold text-primary hover:bg-muted/40 transition-all disabled:opacity-60"
                    >
                      {t(msg`Load more results`)}
                    </button>
                  </Show>
                  <NativeAdCard placement="dictionary-catalog" minHeight="6rem" />
                </div>
              </Show>
            </Suspense>
          </div>
        </div>

        {/* Right Detail Pane - Hidden on mobile if no word is selected */}
        <div
          class="flex-1 overflow-y-auto p-4 md:p-8 flex flex-col bg-background"
          classList={{
            "hidden md:flex": !selectedWord(),
            flex: Boolean(selectedWord()),
          }}
        >
          <Show
            when={selectedWord()}
            fallback={
              <div class="flex-1 flex flex-col items-center justify-center text-center p-6 border border-dashed border-border/30 rounded-2xl bg-card/5 my-auto max-w-lg mx-auto w-full">
                <Info class="h-10 w-10 text-muted-foreground/50 mb-3" />
                <h3 class="text-sm font-bold text-foreground/85">{t(msg`Term Definition`)}</h3>
                <p class="text-xs text-muted-foreground max-w-sm mt-1">
                  {t(
                    msg`Select any Strong code from the list on the left to see its meaning, pronunciation, and complete biblical uses.`,
                  )}
                </p>
              </div>
            }
          >
            <Suspense
              fallback={
                <div class="flex-1 flex flex-col items-center justify-center gap-2.5 py-12 text-sm text-muted-foreground animate-pulse">
                  <RefreshCw class="w-5 h-5 animate-spin" />
                  <span>{t(msg`Loading definition...`)}</span>
                </div>
              }
            >
              <Show
                when={definition()}
                fallback={
                  <div class="p-6 bg-red-500/10 border border-red-500/20 rounded-xl text-red-500 text-sm">
                    {t(msg`Could not load the definition for the selected term.`)}
                  </div>
                }
              >
                {(def) => (
                  <div class="max-w-2xl w-full mx-auto space-y-6">
                    <div class="flex items-center gap-4 border-b border-border/30 pb-4">
                      {/* Mobile Back Button */}
                      <button
                        type="button"
                        onClick={() => setSelectedWord(null)}
                        class="md:hidden p-1.5 rounded-lg hover:bg-muted text-muted-foreground mr-1"
                        aria-label={t(msg`Back to search`)}
                      >
                        <X class="w-4 h-4" />
                      </button>
                      <h2 class="text-4xl font-black text-primary font-mono select-all">
                        {def().word}
                      </h2>
                      <span class="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary uppercase tracking-wide">
                        {def().word.startsWith("G")
                          ? t(msg`Greek (New Testament)`)
                          : t(msg`Hebrew (Old Testament)`)}
                      </span>
                    </div>

                    <div class="space-y-4">
                      <h3 class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        {t(msg`Definition`)}
                      </h3>
                      {/* Render trusted Strong's Dictionary HTML via ref (avoids JSX innerHTML prop) */}
                      {/* Delegación de clicks sobre los <a> internos (que ya son
                          accesibles por teclado). El div es solo la superficie de
                          delegación, no un control interactivo propio. */}
                      {/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- delegation to internal keyboard-accessible anchors */}
                      <div
                        ref={(el) => {
                          createEffect(() => {
                            el.innerHTML = def().definition;
                          });
                        }}
                        onClick={(e) => {
                          handleDefinitionLinkClick(e, setSelectedWord);
                        }}
                        class="text-[0.95rem] leading-[1.75] text-foreground/90 bg-card/50 border border-border/30 rounded-2xl p-6 shadow-sm select-text font-serif prose dark:prose-invert max-w-none
                               prose-p:my-2 prose-strong:text-primary prose-strong:font-bold [&_a]:text-accent [&_a]:font-semibold [&_a]:underline [&_a]:decoration-accent/40"
                      />
                    </div>
                  </div>
                )}
              </Show>
            </Suspense>
          </Show>
        </div>
      </div>
    </div>
  );
}

type DictionarySearch = {
  /** Selected Strong word/code, e.g. `H1254`. */
  word?: string;
  /** Search query in dictionary. */
  q?: string;
};

export const dictionaryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dictionary",
  validateSearch: (search: Record<string, unknown>): DictionarySearch => ({
    word:
      typeof search.word === "string" && search.word.trim() !== "" ? search.word.trim() : undefined,
    q: typeof search.q === "string" && search.q.trim() !== "" ? search.q : undefined,
  }),
  component: DictionaryPage,
});
